import * as React from 'react';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { getQueryStringServiceFromAngular } from '../../../common/react/QueryString';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { shouldResetPreviousUrl } from '../../../ts/helpers/UrlHelpers';
import { DataSources } from '../../../ts/resources/Datasources';
import { GettingStartedNotifications } from '../../../ts/services/GettingStartedNotifications';
import { MastheadNotifications } from '../../../ts/services/MastheadNotifications';
import { PostLoginNotifications } from '../../../ts/services/PostLoginNotifications';
import { SiteSwitchService } from '../../../ts/services/SiteSwitchService';
import { StartPageService } from '../../../ts/services/StartPageService';
import { Telemetry } from '../../../ts/services/TelemetryService';
import { incrementRouterSpaceReactKey } from '../../actions/spaces/IncrementRouterSpaceReactKeyAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '../../actions/spaces/SetPreviousUrlReducerAction';
import { appStore } from '../../AppStore';
import { bindDisplaySettingsScopeToReduxAndSetInitialValues } from '../../content/DisplaySettingsReduxBindings';
import { bindDisplaySettingsScopeToUrlAndSetInitialValues } from '../../content/DisplaySettingsUrlBindings';
import { ConnectedLeftNav } from '../../layout/ConnectedLeftNav';
import { ConnectedPageHeader } from '../../layout/ConnectedPageHeader';
import { ConnectedSkipBarProvider } from '../../layout/SkipBarProvider';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { isMetricSpace } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { CustomDragLayer } from '../CustomDragLayer';
import { ServerSpaceRouter } from '../ServerSpaces/ServerSpaceRouter';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { SiteSpaceRouter, SiteSpaceRouterComponent } from './SiteSpaceRouter';

export interface MainOwnProps extends ReactDirectiveProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly fromUnitTests?: boolean; // Hooks don't work well with shallow tests so dirty workaround
}

interface MainPropsFromAngular {
  readonly telemetry: Telemetry;
  readonly $location: ng.ILocationService;
  readonly dataSources: DataSources;
  readonly siteSwitchService: SiteSwitchService;
  readonly startPageService: StartPageService;
  readonly postLoginNotifications: PostLoginNotifications;
  readonly gettingStartedNotifications: GettingStartedNotifications;
  readonly mastheadNotifications: MastheadNotifications;
}

export interface MainStateProps {
  readonly urls: SpacesSiteUrls;
  readonly isMetricSpace: boolean;
}

export interface MainDispatchProps {
  readonly setPreviousUrl: (url: string) => void;
  readonly incrementRouterSpaceReactKey: () => void;
  readonly requestSpaceContents: () => void;
}

export type MainProps = MainOwnProps & MainPropsFromAngular & MainStateProps & MainDispatchProps;

export const MainComponent: React.FunctionComponent<MainProps> = props => {

  const siteNameFromCurrentPath = parseOutSiteUrlName(props.routeLocationPath);
  const siteNameFromRedux = parseOutSiteUrlName(props.urls.homePath());
  const [path, setPath] = React.useState(siteNameFromCurrentPath === siteNameFromRedux ? props.routeLocationPath : '');

  if (!props.fromUnitTests) {
    React.useEffect(() => {
      const queryString = getQueryStringServiceFromAngular(props.$location);
      bindDisplaySettingsScopeToReduxAndSetInitialValues(props.angularContext.scope as any, appStore);
      bindDisplaySettingsScopeToUrlAndSetInitialValues(props.angularContext.scope as any, appStore, queryString);
    }, []);
  }

  if (props.postLoginNotifications) {
    React.useEffect(() => {
      // See http://mytableau/display/devft/Vizportal+Online+Notifications+Spec
      props.postLoginNotifications.showNotificationsIfEnabled()
        .finally(() => props.gettingStartedNotifications.showNotificationsIfEnabled())
        .finally(() => props.mastheadNotifications.showNotificationsIfEnabled());
    }, []);
  }

  React.useEffect(() => {
    if (props.routeLocationPath !== path) {
      const siteNameFromCurrentPath = parseOutSiteUrlName(props.routeLocationPath);
      const isChangingSites = parseOutSiteUrlName(path) !== siteNameFromCurrentPath;
      if (isChangingSites && !_.isUndefined(siteNameFromCurrentPath)) {
        props.siteSwitchService.switchIfNeeded(siteNameFromCurrentPath)
          .finally(() => {
            setPath(props.routeLocationPath);
          });
      } else {
        setPath(props.routeLocationPath);
      }

      // Includes resetting when navigating to /server which isChangingSites will catch
      if (shouldResetPreviousUrl(props.routeLocationPath, isChangingSites)){
        props.setPreviousUrl('');
      }
    }
  }, [props.routeLocationPath]);

  if (props.fromUnitTests) { return null; }

  const constrainedClass = props.isMetricSpace ? '' : 'tb-main-constrained';
  return (
    <div className={'tb-column-flex-parent tb-fill'}>
      <ConnectedSkipBarProvider />
      <AngularDirective template={'<tb:app-banner />'} angularContext={props.angularContext} />
      <div className={'tb-flex-fill ' + constrainedClass}>
        <div className={'tb-row-flex-parent tb-full-height'}>
          <ConnectedLeftNav telemetryService={props.telemetry}/>
          <div className={'tb-flex-fill tb-column-flex-parent tb-main-content'}>
            <ConnectedPageHeader telemetryService={props.telemetry} angularContext={props.angularContext} datasourcesResource={props.dataSources}/>
            <div className={'tb-flex-fill'}>
              <DndProvider backend={HTML5Backend}>
                <CustomDragLayer/>
                <Switch>
                  <Route exact from={props.urls.defaultPath()} render={() => {
                    const startPagePath = getDefaultRedirectPath(props.routeLocationPath, props.startPageService);
                    return <Redirect exact from={props.urls.defaultPath()} to={startPagePath} />;
                  }}/>
                   <Route exact from={'/'} render={() => {
                    const startPagePath = getDefaultRedirectPath(props.routeLocationPath, props.startPageService);
                    return <Redirect exact from={props.urls.defaultPath()} to={startPagePath} />;
                  }}/>
                  <Route path={'/server'} render={() => <ServerSpaceRouter
                      angularContext={props.angularContext}
                      routeLocationPath={props.routeLocationPath}
                      routeLocationSearch={props.routeLocationSearch}
                    />
                  }/>
                  <Route render={() => <SiteSpaceRouter
                      isBusy={props.routeLocationPath !== path}
                      siteName={siteNameFromCurrentPath}
                      angularContext={props.angularContext}
                      routeLocationPath={path} // Don't want to render until the site switch is done and path has been updated
                      routeLocationSearch={props.routeLocationSearch}
                    />
                  }/>
                </Switch>
              </DndProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const parseOutSiteUrlName = (path: string): string  => {
  if (path.startsWith('/site/')) {
    path = path.substring(6); // chop off the initial '/site/'
    const endSiteNamePattern = new RegExp('[/?]');
    const endSiteNameIndex = path.search(endSiteNamePattern);
    if (endSiteNameIndex === -1) {
      return path === '' ? undefined : path;
    }
    const siteUrlName = path.substring(0, endSiteNameIndex);
    return siteUrlName === '' ? undefined : siteUrlName;
  }

  // if its a valid site space path return empty string to indicate default
  const urls = new SpacesSiteUrls({ siteUrlName: '' });
  const basePaths = SiteSpaceRouterComponent.getWatchedBasePaths(urls);
  if (_.some(basePaths, (basePath) => path.startsWith(basePath))) {
    return '';
  }

  // these should be /server/ paths or invalid paths
  return undefined;
};

const getDefaultRedirectPath = (routeLocationPath: string, startPageService: StartPageService): string => {
  const siteUrlName = parseOutSiteUrlName(routeLocationPath);
  if (!_.isUndefined(siteUrlName) && siteUrlName.length > 0) {
    return startPageService.getCurrentUserStartPageForSite(siteUrlName);
  } else {
    return startPageService.getCurrentUserStartPageForCurrentSite();
  }
};

export namespace MainAngularProvider {
  function getAngularServicesWithServiceLocator() {
    return {
      telemetry: ServiceLocator.get<Telemetry>('TelemetryService'),
      dataSources: ServiceLocator.get<DataSources>('DataSources'),
      $location: ServiceLocator.get<ng.ILocationService>('$location'),
      siteSwitchService: ServiceLocator.get<SiteSwitchService>('SiteSwitchService'),
      startPageService: ServiceLocator.get<StartPageService>('StartPageService'),
      postLoginNotifications: ServiceLocator.get<PostLoginNotifications>('PostLoginNotifications'),
      gettingStartedNotifications: ServiceLocator.get<GettingStartedNotifications>('GettingStartedNotifications'),
      mastheadNotifications: ServiceLocator.get<MastheadNotifications>('MastheadNotifications'),
    };
  }

  function selectPropsFromAngularServices(angularServices: MainPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<MainOwnProps, MainPropsFromAngular, MainPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const mapStateToProps: MapStateToProps<MainStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  isMetricSpace: isMetricSpace(selectSpaceInfo(state))
});

const mapDispatchToProps: MapDispatchToProps<MainDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey,
  setPreviousUrl,
  requestSpaceContents,
}, dispatch);

export const ConnectedMainComponent: React.ComponentClass<MainOwnProps & MainPropsFromAngular>
  = connect(mapStateToProps, mapDispatchToProps)(MainComponent);

export const Main: React.ComponentType<MainOwnProps> = MainAngularProvider.HOC(ConnectedMainComponent);
